/**
 * ProjectVaultTab – Inline vault entries for a specific project.
 * Shows passwords linked to this project with quick-reveal and admin create button.
 */
import { useState, useEffect, useCallback } from "react";
import {
  Shield, Plus, Eye, EyeOff, Copy, Loader2,
  Wifi, Users, Monitor, Lock, Tag, ExternalLink, ClipboardCopy,
  Key, Globe, Server, Database, Settings, Home, Building2, Phone,
  Mail, Cloud, File, Folder, Star, Heart, Zap, Wrench, Cpu, HardDrive,
  Printer, Camera, Bluetooth, Headphones,
} from "lucide-react";
import { toast } from "sonner";

const API = "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, ...(opts.headers || {}) };
  if (!(opts.body instanceof FormData)) headers["Content-Type"] = "application/json";
  return fetch(url, { ...opts, headers });
}

const ICON_MAP = {
  wifi: Wifi, users: Users, monitor: Monitor, lock: Lock, tag: Tag, key: Key,
  globe: Globe, server: Server, database: Database, shield: Shield,
  settings: Settings, home: Home, building: Building2, phone: Phone,
  mail: Mail, cloud: Cloud, file: File, folder: Folder, star: Star,
  heart: Heart, zap: Zap, tool: Wrench, cpu: Cpu, "hard-drive": HardDrive,
  printer: Printer, camera: Camera, bluetooth: Bluetooth, headphones: Headphones,
};

function getIconComponent(iconName) { return ICON_MAP[iconName] || Tag; }

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert`);
  } catch {
    toast.error("Kopieren fehlgeschlagen");
  }
}

export default function ProjectVaultTab({ projectId, projectName, isAdmin }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealedId, setRevealedId] = useState(null);
  const [revealedData, setRevealedData] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [categories, setCategories] = useState([]);

  const loadEntries = useCallback(async () => {
    try {
      const [entryRes, catRes] = await Promise.all([
        authFetch(`${API}/vault/project/${projectId}`),
        authFetch(`${API}/vault/categories`),
      ]);
      if (entryRes.ok) setEntries(await entryRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch {}
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const revealPassword = async (entryId) => {
    if (revealedId === entryId) { setRevealedId(null); setRevealedData(null); return; }
    setRevealing(true);
    try {
      const res = await authFetch(`${API}/vault/${entryId}/reveal`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setRevealedId(entryId);
        setRevealedData(data);
      } else toast.error("Zugriff verweigert");
    } catch { toast.error("Fehler"); }
    setRevealing(false);
  };

  const quickCopyEntry = async (entryId) => {
    try {
      const res = await authFetch(`${API}/vault/${entryId}/reveal`, { method: "POST" });
      if (!res.ok) { toast.error("Zugriff verweigert"); return; }
      const data = await res.json();
      const text = [data.username, data.password].filter(Boolean).join("\n");
      await navigator.clipboard.writeText(text);
      toast.success("Zugangsdaten kopiert");
    } catch { toast.error("Fehler"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="project-vault-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-white">{entries.length} Zugangsdaten</span>
        </div>
        {isAdmin && (
          <a href={`/app/passwort-tresor?create=true&projectId=${projectId}&projectName=${encodeURIComponent(projectName || "")}`}
            className="flex items-center gap-1 text-xs text-blue-400 font-semibold active:text-blue-300"
            data-testid="project-vault-add"
          >
            <Plus className="w-3.5 h-3.5" />Hinzufuegen
          </a>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-8 bg-white/[0.03] border border-white/8 rounded-xl">
          <Shield className="w-8 h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-xs text-gray-500">Keine Zugangsdaten fuer dieses Projekt</p>
          {isAdmin && (
            <a href={`/app/passwort-tresor?create=true&projectId=${projectId}&projectName=${encodeURIComponent(projectName || "")}`}
              className="inline-flex items-center gap-1 mt-2 text-xs text-blue-400 font-semibold"
              data-testid="project-vault-add-empty"
            >
              <Plus className="w-3 h-3" />Jetzt anlegen
            </a>
          )}
        </div>
      ) : (
        entries.map(entry => {
          const catConf = categories.find(c => c.slug === entry.category) || { icon: "tag", color: "gray" };
          const CatIcon = getIconComponent(catConf.icon);
          const isRevealed = revealedId === entry.id;

          return (
            <div key={entry.id} className="bg-white/[0.04] border border-white/8 rounded-xl overflow-hidden" data-testid={`project-vault-entry-${entry.id}`}>
              {/* Entry header */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-${catConf.color}-500/15`}>
                  <CatIcon className={`w-4 h-4 text-${catConf.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{entry.title}</p>
                  {entry.username && <p className="text-xs text-gray-500 truncate font-mono">{entry.username}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => quickCopyEntry(entry.id)}
                    className="p-2 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors"
                    data-testid={`project-vault-copy-${entry.id}`}
                  >
                    <ClipboardCopy className="w-3.5 h-3.5 text-emerald-400" />
                  </button>
                  <button onClick={() => revealPassword(entry.id)}
                    disabled={revealing}
                    className="p-2 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors"
                    data-testid={`project-vault-reveal-${entry.id}`}
                  >
                    {revealing && !isRevealed ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" /> :
                      isRevealed ? <EyeOff className="w-3.5 h-3.5 text-amber-400" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                  </button>
                </div>
              </div>

              {/* Revealed data */}
              {isRevealed && revealedData && (
                <div className="px-4 py-3 bg-amber-500/[0.04] border-t border-amber-500/10 space-y-2">
                  {revealedData.username && (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase">Benutzer</p>
                        <p className="text-xs text-white font-mono">{revealedData.username}</p>
                      </div>
                      <button onClick={() => copyToClipboard(revealedData.username, "Benutzername")} className="p-1.5"><Copy className="w-3 h-3 text-gray-500" /></button>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Passwort</p>
                      <p className="text-xs text-white font-mono">{revealedData.password}</p>
                    </div>
                    <button onClick={() => copyToClipboard(revealedData.password, "Passwort")} className="p-1.5"><Copy className="w-3 h-3 text-gray-500" /></button>
                  </div>
                  {entry.url && (
                    <a href={entry.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-400 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />{entry.url}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
